import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { daysFromNow } from '@zapier/date';
import { useIsAuthenticated } from '@zapier/design-system-revenue/src/hooks';

import { setTeamAutoUpgrade as SetTeamAutoUpgradeMutation } from './types/setTeamAutoUpgrade';
import { teamTrialBannerAccount as AccountQuery } from './types/teamTrialBannerAccount';
import { teamTrialBannerNextPlan as NextPlanQuery } from './types/teamTrialBannerNextPlan';

const accountQuery = gql`
  query teamTrialBannerAccount($accountId: Int!) {
    account(id: $accountId) {
      currentUserRole
      id
      plan: planV2 {
        id
        interval
      }
      teamsTrialAutoUpgrade
      teamTrialEndDate
    }
  }
`;

const nextPlanQuery = gql`
  query teamTrialBannerNextPlan(
    $accountId: Int!
    $interval: BillingInterval!
    $type: String!
  ) {
    nextPlan: nextPlanV2(
      accountId: $accountId
      interval: $interval
      type: $type
    ) {
      amount
      id
    }
  }
`;

export const setTeamAutoUpgradeMutation = gql`
  mutation setTeamAutoUpgrade($accountId: Int!, $shouldAutoUpgrade: Boolean!) {
    setTeamAutoUpgrade(id: $accountId, shouldAutoUpgrade: $shouldAutoUpgrade) {
      id
      teamsTrialAutoUpgrade
    }
  }
`;

export type Props = {
  skip?: boolean;
};

export const useData = ({ skip }: Props = {}) => {
  const { isAuthenticated } = useIsAuthenticated();

  const accountId = parseInt(localStorage.getItem('currentAccountId') ?? '');

  const {
    data: accountData,
    error: accountError,
    loading: isLoadingAccount,
  } = useQuery<AccountQuery>(accountQuery, {
    skip: skip || !isAuthenticated || !accountId,
    variables: {
      accountId,
    },
  });

  const account = accountData?.account;
  const trialEndDate = account?.teamTrialEndDate;

  const [toggleTeamAutoUpgradeMutation, { error: mutationError }] = useMutation<
    SetTeamAutoUpgradeMutation
  >(setTeamAutoUpgradeMutation);

  const toggleTeamAutoUpgrade = () =>
    toggleTeamAutoUpgradeMutation({
      variables: {
        accountId,
        shouldAutoUpgrade: !account?.teamsTrialAutoUpgrade,
      },
    });

  const {
    data: nextPlanData,
    error: nextPlanError,
    loading: isLoadingNextPlan,
  } = useQuery<NextPlanQuery>(nextPlanQuery, {
    skip: !account,
    variables: {
      accountId,
      interval: account?.plan?.interval,
      type: 'Team',
    },
  });

  const billingInterval = account?.plan?.interval;
  const nextPlan = nextPlanData?.nextPlan;

  return {
    accountId: account?.id,
    billingInterval: account?.plan?.interval,
    error:
      accountError || nextPlanError || mutationError
        ? 'An error has occurred. Please try again.'
        : null,
    isLoading: isLoadingAccount || isLoadingNextPlan,
    shouldAutoUpgrade: account?.teamsTrialAutoUpgrade,
    shouldRenderToggle: ['admin', 'owner'].includes(
      account?.currentUserRole ?? ''
    ),
    teamPlanMonthlyAmount: nextPlan?.amount
      ? billingInterval === 'year'
        ? nextPlan.amount / 12
        : nextPlan.amount
      : 0,
    toggleTeamAutoUpgrade,
    trialDaysRemaining: trialEndDate ? daysFromNow(trialEndDate) : 0,
  };
};
